<template>
    <e-mobile-modal :value="value" fullscreen hide-close :close-on-backdrop="false"
        panel-class="map-mobile-qr-scanner-panel" dialog-class="map-mobile-qr-scanner-dialog" body-no-padding
        @input="$emit('input', $event)" @closed="onClosed">

        <div v-if="error" class="map-mobile-qr-scanner">
            <div class="scanner-overlay scanner-overlay-static">
                <div class="scanner-top">
                    <b-btn variant="text" pill size="lg" class="text-white ratio-1x1" @click="closeScanner">
                        <i :class="[fontAwesomePrefix, 'fa-arrow-left']" />
                    </b-btn>
                </div>

                <div class="scanner-stage">
                    <div class="scanner-frame-shell">
                        <div class="scanner-frame-box">
                            <div class="scanner-frame-corners">
                                <span class="corner corner-top-left" />
                                <span class="corner corner-top-right" />
                                <span class="corner corner-bottom-left" />
                                <span class="corner corner-bottom-right" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="scanner-error">
                    <div class="scanner-title"> {{ $t('map.assistant.qrCodeScanner.cameraUnavailable') }} </div>
                    <div class="scanner-error-text"> {{ $t('map.assistant.qrCodeScanner.cameraUnavailableText') }} </div>
                </div>
            </div>
        </div>

        <qrcode-stream v-else class="map-mobile-qr-scanner" :camera="camera" :track="trackQrOutline" @init="onInit">
            <div class="scanner-overlay">
                <div class="scanner-top">
                    <b-btn variant="text" pill size="lg" class="text-white ratio-1x1" @click="closeScanner">
                        <i :class="[fontAwesomePrefix, 'fa-arrow-left']" />
                    </b-btn>
                </div>

                <div class="scanner-stage">
                    <div class="scanner-frame-shell">
                        <div class="scanner-frame-box">
                            <div class="scanner-frame-corners">
                                <span class="corner corner-top-left" />
                                <span class="corner corner-top-right" />
                                <span class="corner corner-bottom-left" />
                                <span class="corner corner-bottom-right" />
                            </div>
                            <div class="scanner-sweep" />
                        </div>
                    </div>

                    <e-toast :visible="invalidQrToastVisible" :message="$t(`map.assistant.qrCodeScanner.invalidQrCode`)" variant="warning" size="xs" :render-in-body-for-wrapper="false"
                        position="bottom-center" as-wrapper translucent :auto-close-ms="invalidQrToastAutoCloseMs" offset-y="24px"
                        @update:visible="onInvalidQrToastVisibleChange">

                            <div class="scanner-title mt-5">
                                {{ $t('map.assistant.qrCodeScanner.title') }}
                            </div>
                    </e-toast>
                </div>

                <div v-if="loading" class="scanner-loading">
                    <div class="scanner-loading-spinner">
                        <i :class="[fontAwesomePrefix, 'fa-spinner-third', 'fa-spin']" />
                    </div>
                </div>
            </div>
        </qrcode-stream>
    </e-mobile-modal>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import EMobileModal from '@/components/e-mobile-modal.vue';
import EToast from '@/components/e-toast.vue';
import NetworkV2 from '@vue-components/helpers/NetworkV2.js';

function drawDetectedQrPath(code, ctx) {
    const points = code.cornerPoints && code.cornerPoints.length ? code.cornerPoints : null;
    if (!points) {
        return;
    }

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let index = 1; index < points.length; index += 1) {
        ctx.lineTo(points[index].x, points[index].y);
    }
    ctx.closePath();
    ctx.stroke();

    for (let index = 0; index < points.length; index += 1) {
        const point = points[index];
        ctx.beginPath();
        ctx.arc(point.x, point.y, 7, 0, 2 * Math.PI);
        ctx.fill();
    }
}

function paintQrOutline(codes, ctx) {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.lineWidth = 5;
    ctx.strokeStyle = `#ffd400`;
    ctx.fillStyle = `#ffd400`;

    for (let index = 0; index < codes.length; index += 1) {
        drawDetectedQrPath(codes[index], ctx);
    }
}

const INVALID_QR_TOAST_AUTO_CLOSE_MS = 200;
const BLOCKED_QR_DETECTION_IDLE_MS = 180;

export default {
    name: `map-mobile-qr-scanner`,
    components: { EMobileModal, EToast, QrcodeStream },
    props: {
        value: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: true,
            error: null,
            camera: `auto`,
            originalEnumerateDevices: null,
            activeQrValue: null,
            activeQrDetectionTimeout: null,
            processingQrValue: null,
            blockedQrValue: null,
            blockedQrDetected: false,
            blockedQrDetectionTimeout: null,
            invalidQrToastVisible: false
        };
    },
    computed: {
        invalidQrToastAutoCloseMs() {
            if (!this.invalidQrToastVisible || this.blockedQrDetected) {
                return 0;
            }

            return INVALID_QR_TOAST_AUTO_CLOSE_MS;
        }
    },
    watch: {
        value(value) {
            if (value) {
                this.resetScanner();
                this.installRearCameraFilter();
                return;
            }
            this.camera = `off`;
            this.restoreRearCameraFilter();
        }
    },
    beforeDestroy() {
        this.restoreRearCameraFilter();
    },
    methods: {
        installRearCameraFilter() {
            if (this.originalEnumerateDevices) {
                return;
            }

            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                return;
            }

            // Override device listing while scanner is open so auto mode prefers rear wide camera.
            this.originalEnumerateDevices = navigator.mediaDevices.enumerateDevices.bind(navigator.mediaDevices);
            navigator.mediaDevices.enumerateDevices = this.getFilteredDevices;
        },
        restoreRearCameraFilter() {
            if (!this.originalEnumerateDevices) {
                return;
            }

            navigator.mediaDevices.enumerateDevices = this.originalEnumerateDevices;
            this.originalEnumerateDevices = null;
        },
        async getFilteredDevices() {
            const devices = await this.originalEnumerateDevices();
            const videos = devices.filter(device => device.kind === `videoinput`);
            const others = devices.filter(device => device.kind !== `videoinput`);
            // Keep only rear-labeled cameras, then prefer non tele/zoom variants.
            const rearVideos = videos.filter(video => /(back|rear|environment|world)/.test(video.label.toLowerCase()));
            const preferredRear = rearVideos.find(video => !/(tele|zoom|ultra|triple|dual|macro|periscope|3x|5x|10x)/.test(video.label.toLowerCase())) || rearVideos[rearVideos.length - 1];

            if (!preferredRear) {
                return devices;
            }

            // Put preferred rear camera last because vue-qrcode-reader auto mode picks the last rear entry.
            const orderedVideos = videos.filter(video => video.deviceId !== preferredRear.deviceId);
            orderedVideos.push(preferredRear);
            return orderedVideos.concat(others);
        },
        // Extract the short link id from a scanned QR value.
        getShortLink(value) {
            if (!value) {
                return null;
            }

            try {
                const url = new URL(value, window.location.origin);
                const shortLinkMatch = url.pathname.match(/^\/s\/([^/]+)\/?$/i);
                if (!shortLinkMatch || !shortLinkMatch[1]) {
                    return null;
                }

                const query = {};
                for (const [key, entryValue] of url.searchParams.entries()) {
                    query[key] = entryValue;
                }

                return {
                    id: decodeURIComponent(shortLinkMatch[1]),
                    query
                };
            } catch {
                return null;
            }
        },
        // Resolve the scanned short link before map handling.
        async getResolvedQrValue(value) {
            const shortLink = this.getShortLink(value);
            if (!shortLink) {
                return null;
            }

            const response = await NetworkV2.get(
                `/api/public/shortener/resolve/${encodeURIComponent(shortLink.id)}`,
                shortLink.query
            );

            return response.data.url;
        },
        // Read query parameters from a resolved QR URL.
        getQrSearchParams(value) {
            try {
                const url = new URL(value, window.location.origin);
                return url.searchParams;
            } catch {
                try {
                    return new URLSearchParams(value);
                } catch {
                    return null;
                }
            }
        },
        // Check whether the resolved QR URL points to a supported map target.
        isSupportedQrTarget(value) {
            const searchParams = this.getQrSearchParams(value);
            if (!searchParams) {
                return false;
            }

            const qrCode = searchParams.get(`qr`);
            if (qrCode) {
                return true;
            }

            const startPermalink = searchParams.get(`start`) || searchParams.get(`depart`);
            const endPermalink = searchParams.get(`end`) || searchParams.get(`arrivee`);
            return !!(startPermalink && endPermalink);
        },
        // Reject the current QR value and avoid scanning it again in a loop.
        rejectQrValue(value) {
            this.blockedQrValue = value;
            this.blockedQrDetected = true;
            this.invalidQrToastVisible = true;
            this.queueBlockedQrRelease();
            this.resumeScanner();
        },
        resetScanner() {
            this.loading = true;
            this.error = null;
            this.camera = `auto`;
            this.activeQrValue = null;
            this.clearActiveQrDetectionTimeout();
            this.processingQrValue = null;
            this.blockedQrValue = null;
            this.blockedQrDetected = false;
            this.clearBlockedQrDetectionTimeout();
            this.invalidQrToastVisible = false;
        },
        closeScanner() {
            this.$emit(`input`, false);
        },
        async onInit(promise) {
            try {
                await promise;
                this.loading = false;
                this.error = null;
            } catch (error) {
                this.loading = false;
                this.error = error;
            }
        },
        // Decode the QR code and pass the resolved URL to the map view.
        async onDecode(value) {
            if (this.processingQrValue || value === this.blockedQrValue) {
                return;
            }

            this.processingQrValue = value;
            this.loading = true;

            try {
                const resolvedValue = await this.getResolvedQrValue(value);
                if (!resolvedValue || !this.isSupportedQrTarget(resolvedValue)) {
                    this.rejectQrValue(value);
                    return;
                }

                this.blockedQrValue = null;
                this.$emit(`decode`, resolvedValue, this.resumeScanner, () => this.rejectQrValue(value));
            } catch {
                this.rejectQrValue(value);
            }
        },
        resumeScanner() {
            this.loading = false;
            this.error = null;
            this.processingQrValue = null;
        },
        trackQrOutline(codes, ctx) {
            paintQrOutline(codes, ctx);

            const currentQrValue = codes[0] && codes[0].rawValue;
            if (currentQrValue) {
                this.queueActiveQrRelease();
            }

            if (!this.blockedQrValue) {
                if (!currentQrValue || currentQrValue === this.activeQrValue || currentQrValue === this.processingQrValue) {
                    return;
                }

                this.activeQrValue = currentQrValue;
                this.onDecode(currentQrValue);
                return;
            }

            for (let index = 0; index < codes.length; index += 1) {
                if (codes[index].rawValue === this.blockedQrValue) {
                    this.blockedQrDetected = true;
                    this.queueBlockedQrRelease();
                    return;
                }
            }

            if (!currentQrValue || currentQrValue === this.blockedQrValue || currentQrValue === this.activeQrValue || currentQrValue === this.processingQrValue) {
                return;
            }

            this.activeQrValue = currentQrValue;
            this.onDecode(currentQrValue);
        },
        queueActiveQrRelease() {
            this.clearActiveQrDetectionTimeout();
            this.activeQrDetectionTimeout = setTimeout(() => {
                this.activeQrValue = null;
                this.activeQrDetectionTimeout = null;
            }, BLOCKED_QR_DETECTION_IDLE_MS);
        },
        clearActiveQrDetectionTimeout() {
            if (!this.activeQrDetectionTimeout) {
                return;
            }

            clearTimeout(this.activeQrDetectionTimeout);
            this.activeQrDetectionTimeout = null;
        },
        queueBlockedQrRelease() {
            this.clearBlockedQrDetectionTimeout();
            this.blockedQrDetectionTimeout = setTimeout(() => {
                this.blockedQrDetected = false;
                this.blockedQrDetectionTimeout = null;
            }, BLOCKED_QR_DETECTION_IDLE_MS);
        },
        clearBlockedQrDetectionTimeout() {
            if (!this.blockedQrDetectionTimeout) {
                return;
            }

            clearTimeout(this.blockedQrDetectionTimeout);
            this.blockedQrDetectionTimeout = null;
        },
        onInvalidQrToastVisibleChange(value) {
            this.invalidQrToastVisible = value;

            if (!value) {
                this.blockedQrValue = null;
                this.blockedQrDetected = false;
                this.clearBlockedQrDetectionTimeout();
            }
        },
        onClosed() {
            this.camera = `off`;
            this.restoreRearCameraFilter();
            this.loading = true;
            this.error = null;
            this.activeQrValue = null;
            this.clearActiveQrDetectionTimeout();
            this.processingQrValue = null;
            this.blockedQrValue = null;
            this.blockedQrDetected = false;
            this.clearBlockedQrDetectionTimeout();
            this.$emit(`closed`);
        }
    }
};
</script>

<style lang="scss" scoped>
.map-mobile-qr-scanner {
    position: relative;
    width: 100%;
    height: 100%;
    background: #020508;
}

.scanner-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    padding: calc(env(safe-area-inset-top, 0px) + 12px) 16px calc(env(safe-area-inset-bottom, 0px) + 18px);
    background: transparent;
    pointer-events: none;
}

.scanner-overlay-static {
    background: transparent;
}

.scanner-top {
    display: flex;
    align-items: center;
    gap: 12px;
    pointer-events: auto;
    z-index: 3;
}

.tiny-nav-top {
    display: flex;
    align-items: center;
    min-height: 42px;
    padding: 0 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(12px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
}

.tiny-nav-top-content {
    display: flex;
    align-items: center;
}

.tiny-nav-top-title {
    font-size: 11pt;
    font-weight: 600;
    color: #13202c;
}

.scanner-stage {
    --scanner-frame-size: min(60vw, 308px);
    --corner-radius: 32px;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: start;
    padding-top: 16dvh;
    flex: 1 1 auto;
}

.scanner-frame-shell {
    position: relative;
    z-index: 1;
    width: var(--scanner-frame-size);
    height: var(--scanner-frame-size);
    border-radius: 28px;
    background: transparent;
}

.scanner-frame-box {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 24px;
    overflow: visible;
    background: transparent;
    box-shadow:
        0 0 0 9999px rgba(0, 0, 0, 0.5),
        inset 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.scanner-frame-corners {
    position: absolute;
    inset: -14px;
}

.corner {
    position: absolute;
    width: 40px;
    height: 40px;
    border-color: #ffffff;
    border-style: solid;
}

.corner-top-left {
    top: 0;
    left: 0;
    border-width: 4px 0 0 4px;
    border-top-left-radius: var(--corner-radius);
}

.corner-top-right {
    top: 0;
    right: 0;
    border-width: 4px 4px 0 0;
    border-top-right-radius: var(--corner-radius);
}

.corner-bottom-left {
    bottom: 0;
    left: 0;
    border-width: 0 0 4px 4px;
    border-bottom-left-radius: var(--corner-radius);
}

.corner-bottom-right {
    right: 0;
    bottom: 0;
    border-width: 0 4px 4px 0;
    border-bottom-right-radius: var(--corner-radius);
}

.scanner-sweep {
    position: absolute;
    left: 10%;
    right: 10%;
    top: 18%;
    height: 2px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(125, 240, 210, 0), rgba(125, 240, 210, 0.95), rgba(125, 240, 210, 0));
    animation: scanner-sweep 2.8s ease-in-out infinite;
}

.scanner-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 3;
}

.scanner-loading-spinner {
    width: 62px;
    height: 62px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 26px;
    background: rgba(255, 255, 255, 0.14);
    backdrop-filter: blur(10px);
}

.scanner-error {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    padding: 24px 8px 8px;
    color: white;
    text-align: center;
    z-index: 3;
}

.scanner-title {
    color: white;
    font-size: 17pt;
    font-weight: 600;
    z-index: 2;
    margin-inline: auto;
}

:deep(.scanner-stage .e-toast.wrapper) {
    width: max-content;
    max-width: calc(100vw - 32px);
}

:deep(.scanner-stage .e-toast .message) {
    white-space: nowrap;
}

.scanner-error-text {
    font-size: 11pt;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.82);
}

:deep(.map-mobile-qr-scanner-dialog .e-mobile-modal__body) {
    padding: 0;
}

:deep(.map-mobile-qr-scanner-panel) {
    background: #020508;
    height: 100%;
}

:deep(.qrcode-stream-wrapper) {
    width: 100%;
    height: 100%;
}

:deep(.qrcode-stream-overlay) {
    mix-blend-mode: normal;
}

:deep(video) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

@keyframes scanner-sweep {
    0% {
        top: 18%;
        opacity: 0.3;
    }

    50% {
        top: 78%;
        opacity: 1;
    }

    100% {
        top: 18%;
        opacity: 0.3;
    }
}
</style>
